
/**
 * MapperSidebar — left column listing every Shopify target column ("Export
 * Fields"). This is the surface where the user verifies their export shape:
 *
 * - Default behaviour now is to show ALL columns (including N/A-by-default).
 *   The user explicitly asked for this so nothing important hides silently.
 *   Toggle is inverted: "Hide N/A" (default off → everything visible).
 *
 * - Each row has a status marker derived from `columnStatusFor`:
 *     • mapped (rule exists)        — solid emerald dot (violet if AI)
 *     • module (owned by a Module)  — tone-coloured SQUARE + module chip
 *     • na    (N/A-by-default)      — hollow zinc ring + "N/A" pill
 *     • unset (none of the above)   — hollow slate ring
 *   The Module case is the headline change — previously these columns showed
 *   as "unset", which lied about Image Src etc. being processed.
 *
 * - A top stat strip exposes counts by status; clicking a count filters the
 *   list to that subset. The filter coexists with the search input.
 *
 * - "+ Add custom column" is promoted to the header (next to the title) as a
 *   small primary button; the inline add input still lives in the footer for
 *   keyboard flow, just unlabelled until activated.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { COLUMN_GROUPS, NA_BY_DEFAULT } from "@/lib/mapper/columns";
import type { Profile } from "@/lib/mapper/types";
import { columnStatusFor, TONE_CLASSES, type ColumnStatus } from "./columnStatus";

type Props = {
  profile: Profile;
  selectedColumn: string | null;
  onSelect: (col: string) => void;
  customColumns: string[];
  onAddCustomColumn: (name: string) => void;
  onAutoMap: (opts: { overrideExisting: boolean }) => void;
  /** Set of column names that were just filled by the most recent auto-map run. */
  recentlyAutoMapped: Set<string>;
};

type StatusFilter = "all" | "mapped" | "module" | "unset" | "na";

const HIDE_NA_KEY = "proly.mapper.hideNA";

export function MapperSidebar({
  profile,
  selectedColumn,
  onSelect,
  customColumns,
  onAddCustomColumn,
  onAutoMap,
  recentlyAutoMapped,
}: Props) {
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  // Inverted semantics from the old "Show N/A" toggle. Default is OFF (= show
  // everything). Persisted under a new key so existing users adopt the new
  // default instead of inheriting the old hide-by-default behaviour.
  const [hideNA, setHideNA] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [autoMenuOpen, setAutoMenuOpen] = useState(false);
  const autoMenuRef = useRef<HTMLDivElement>(null);

  // Hydrate the toggle from localStorage on mount. We don't include this in
  // the initial useState so SSR/CSR render the same default first frame.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(HIDE_NA_KEY);
      if (stored === "1") setHideNA(true);
    } catch {
      /* storage disabled — keep default */
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(HIDE_NA_KEY, hideNA ? "1" : "0");
    } catch {
      /* non-fatal */
    }
  }, [hideNA]);

  // Close auto-map dropdown when the user clicks anywhere outside it.
  useEffect(() => {
    if (!autoMenuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!autoMenuRef.current) return;
      if (!autoMenuRef.current.contains(e.target as Node)) {
        setAutoMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [autoMenuOpen]);

  const naSet = useMemo(() => new Set<string>(NA_BY_DEFAULT), []);

  const groups = useMemo(() => {
    const base = COLUMN_GROUPS.map((g) => ({
      label: g.label,
      columns: g.columns as readonly string[],
    }));
    if (customColumns.length > 0) {
      base.push({ label: "Custom", columns: customColumns });
    }
    return base;
  }, [customColumns]);

  // Status index — computed once per (profile, customColumns) tick. Used by
  // both the totals strip and the row renderer so they can never disagree.
  const statusByColumn = useMemo(() => {
    const map: Record<string, ColumnStatus> = {};
    for (const g of groups) {
      for (const c of g.columns) {
        map[c] = columnStatusFor(profile, c, naSet);
      }
    }
    return map;
  }, [groups, profile, naSet]);

  // Counts feed both the header stat strip and the footer line.
  const totals = useMemo(() => {
    let shopifyTotal = 0;
    const customTotal = customColumns.length;
    let mapped = 0;
    let module_ = 0;
    let na = 0;
    let unset = 0;
    for (const g of groups) {
      const isCustom = g.label === "Custom";
      for (const c of g.columns) {
        if (!isCustom) shopifyTotal++;
        const s = statusByColumn[c];
        if (!s) continue;
        switch (s.kind) {
          case "mapped":
            mapped++;
            break;
          case "module":
            module_++;
            break;
          case "na":
            na++;
            break;
          case "unset":
            unset++;
            break;
        }
      }
    }
    return { shopifyTotal, customTotal, mapped, module: module_, na, unset };
  }, [groups, statusByColumn, customColumns.length]);

  const q = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    return groups
      .map((g) => {
        let columns = g.columns;
        if (q) {
          columns = columns.filter((c) => c.toLowerCase().includes(q));
        }
        if (hideNA) {
          // Keep N/A column if it has been mapped or is currently selected so
          // the user doesn't lose their place mid-edit.
          columns = columns.filter(
            (c) =>
              !naSet.has(c) ||
              profile.fields[c] !== undefined ||
              selectedColumn === c,
          );
        }
        if (filter !== "all") {
          columns = columns.filter((c) => {
            const s = statusByColumn[c];
            if (!s) return false;
            return s.kind === filter;
          });
        }
        return { label: g.label, columns };
      })
      .filter((g) => g.columns.length > 0);
  }, [
    groups,
    q,
    hideNA,
    filter,
    naSet,
    profile.fields,
    selectedColumn,
    statusByColumn,
  ]);

  function commitDraft() {
    const name = draft.trim();
    if (!name) {
      setAdding(false);
      setDraft("");
      return;
    }
    onAddCustomColumn(name);
    setDraft("");
    setAdding(false);
  }

  function triggerAutoMap(overrideExisting: boolean) {
    setAutoMenuOpen(false);
    onAutoMap({ overrideExisting });
  }

  return (
    <aside className="flex w-[300px] shrink-0 flex-col border-r border-slate-200 dark:border-border bg-white dark:bg-surface">
      {/* Header / stat strip */}
      <div className="sticky top-0 z-10 border-b border-slate-200 dark:border-border bg-white dark:bg-surface px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
            Export Fields
          </span>
          {adding ? null : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-1.5 py-0.5 text-[10.5px] font-medium text-white transition hover:bg-slate-700 dark:hover:bg-slate-200"
              title="Add a custom column to the export"
            >
              <span aria-hidden="true">+</span>
              Add column
            </button>
          )}
        </div>

        <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-[10.5px] tabular-nums text-slate-500 dark:text-slate-400">
          <span className="text-slate-700 dark:text-slate-300">
            {totals.shopifyTotal} Shopify
          </span>
          <span>+</span>
          <span className="text-slate-700 dark:text-slate-300">{totals.customTotal} custom</span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <CountChip
            label="mapped"
            value={totals.mapped}
            active={filter === "mapped"}
            onClick={() =>
              setFilter((f) => (f === "mapped" ? "all" : "mapped"))
            }
            colorClass="text-emerald-700 dark:text-emerald-300"
          />
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <CountChip
            label="by module"
            value={totals.module}
            active={filter === "module"}
            onClick={() =>
              setFilter((f) => (f === "module" ? "all" : "module"))
            }
            colorClass="text-teal-700 dark:text-teal-300"
          />
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <CountChip
            label="unset"
            value={totals.unset}
            active={filter === "unset"}
            onClick={() =>
              setFilter((f) => (f === "unset" ? "all" : "unset"))
            }
            colorClass="text-slate-700 dark:text-slate-300"
          />
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <CountChip
            label="N/A"
            value={totals.na}
            active={filter === "na"}
            onClick={() => setFilter((f) => (f === "na" ? "all" : "na"))}
            colorClass="text-zinc-500 dark:text-zinc-400"
          />
        </div>

        {adding && (
          <div className="mt-2 flex gap-1">
            <input
              autoFocus
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitDraft();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setAdding(false);
                  setDraft("");
                }
              }}
              placeholder="New column name…"
              className="flex-1 rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[12px] focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600"
            />
            <button
              type="button"
              onClick={commitDraft}
              className="rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-700 dark:hover:bg-slate-200"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setDraft("");
              }}
              className="rounded border border-slate-200 dark:border-border px-1.5 py-1 text-[11px] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
              title="Cancel"
            >
              ✕
            </button>
          </div>
        )}

        <div className="mt-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search columns…"
            className="w-full rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[12px] focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600"
          />
        </div>

        {filter !== "all" && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 rounded bg-slate-100 dark:bg-surface-2 px-1.5 py-0.5 text-[10.5px] text-slate-700 dark:text-slate-300">
            <span>
              Showing: <span className="font-medium">{filterLabel(filter)}</span>
            </span>
            <button
              type="button"
              onClick={() => setFilter("all")}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              aria-label="Clear status filter"
              title="Clear filter"
            >
              clear ×
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="mt-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={hideNA}
              onChange={(e) => setHideNA(e.target.checked)}
              className="h-3 w-3"
            />
            Hide N/A
          </label>
          <div
            ref={autoMenuRef}
            className="relative inline-flex shrink-0 overflow-visible rounded border border-slate-300 dark:border-border bg-white dark:bg-surface shadow-sm"
          >
            <button
              type="button"
              onClick={() => triggerAutoMap(false)}
              title="Match Shopify columns to source headers by name"
              className="inline-flex items-center gap-1 rounded-l px-2 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-surface-2"
            >
              <SparklesIcon />
              Auto-map
            </button>
            <button
              type="button"
              onClick={() => setAutoMenuOpen((o) => !o)}
              aria-label="More auto-map options"
              className="border-l border-slate-300 dark:border-border px-1.5 py-1 text-[11px] text-slate-600 dark:text-slate-400 transition hover:bg-slate-50 dark:hover:bg-surface-2"
            >
              <CaretIcon />
            </button>
            {autoMenuOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 w-60 overflow-hidden rounded border border-slate-200 dark:border-border bg-white dark:bg-surface text-[12px] shadow-lg">
                <button
                  type="button"
                  onClick={() => triggerAutoMap(true)}
                  className="block w-full px-3 py-2 text-left text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-surface-2"
                >
                  Auto-map and replace existing rules
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {filteredGroups.length === 0 && (
          <div className="px-3 py-6 text-center text-[11px] text-slate-400 dark:text-slate-500">
            {q
              ? `No columns match “${search}”.`
              : filter !== "all"
                ? `No columns in this status.`
                : "No columns to show."}
          </div>
        )}
        {filteredGroups.map((group) => (
          <div key={group.label} className="px-1.5 pt-2">
            <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              {group.label}
            </div>
            <ul className="space-y-0.5">
              {group.columns.map((col) => {
                const status = statusByColumn[col] ?? { kind: "unset" as const };
                const active = selectedColumn === col;
                const highlight = recentlyAutoMapped.has(col);
                return (
                  <li key={col}>
                    <button
                      type="button"
                      onClick={() => onSelect(col)}
                      data-automap-highlight={highlight ? "true" : undefined}
                      className={[
                        "group flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[12px] transition",
                        active
                          ? "border-l-2 border-slate-900 dark:border-slate-100 bg-slate-100 dark:bg-surface-2 pl-1.5 text-slate-900 dark:text-slate-100"
                          : "border-l-2 border-transparent pl-1.5 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2",
                        highlight
                          ? "animate-pulse bg-emerald-50/70"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      title={col}
                    >
                      <StatusMark status={status} />
                      <span className="min-w-0 flex-1 truncate font-mono">
                        {col}
                      </span>
                      {status.kind === "module" && (
                        <span
                          className={[
                            "ml-auto shrink-0 inline-flex items-center gap-0.5 rounded px-1 py-px text-[9.5px] font-medium ring-1",
                            TONE_CLASSES[status.module.tone].chip,
                          ].join(" ")}
                          title={status.module.description}
                        >
                          <span aria-hidden="true">{status.module.glyph}</span>
                          <span className="truncate max-w-[88px]">
                            {status.module.name}
                          </span>
                        </span>
                      )}
                      {status.kind === "na" && (
                        <span className="ml-auto shrink-0 rounded bg-zinc-100 dark:bg-surface-2 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                          N/A
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* Footer count line — same filters as the top strip. */}
      <div className="border-t border-slate-200 dark:border-border bg-slate-50 dark:bg-background px-3 py-1.5 text-[10.5px] tabular-nums text-slate-500 dark:text-slate-400">
        <CountChip
          label="mapped"
          value={totals.mapped}
          active={filter === "mapped"}
          onClick={() =>
            setFilter((f) => (f === "mapped" ? "all" : "mapped"))
          }
          colorClass="text-emerald-700 dark:text-emerald-300"
        />
        <span className="px-1 text-slate-300 dark:text-slate-600">·</span>
        <CountChip
          label="module"
          value={totals.module}
          active={filter === "module"}
          onClick={() =>
            setFilter((f) => (f === "module" ? "all" : "module"))
          }
          colorClass="text-teal-700 dark:text-teal-300"
        />
        <span className="px-1 text-slate-300 dark:text-slate-600">·</span>
        <CountChip
          label="unset"
          value={totals.unset}
          active={filter === "unset"}
          onClick={() => setFilter((f) => (f === "unset" ? "all" : "unset"))}
          colorClass="text-slate-700 dark:text-slate-300"
        />
        <span className="px-1 text-slate-300 dark:text-slate-600">·</span>
        <CountChip
          label="N/A"
          value={totals.na}
          active={filter === "na"}
          onClick={() => setFilter((f) => (f === "na" ? "all" : "na"))}
          colorClass="text-zinc-500 dark:text-zinc-400"
        />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Internal components
// ---------------------------------------------------------------------------

function filterLabel(f: StatusFilter): string {
  switch (f) {
    case "mapped":
      return "mapped";
    case "module":
      return "by module";
    case "unset":
      return "unset";
    case "na":
      return "N/A";
    case "all":
      return "all";
  }
}

function CountChip({
  label,
  value,
  active,
  onClick,
  colorClass,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
  colorClass: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Show only ${label} columns`}
      className={[
        "rounded px-1 transition",
        active
          ? "bg-slate-900 dark:bg-slate-100 dark:text-slate-950 text-white"
          : `hover:bg-slate-100 dark:hover:bg-surface-2 ${colorClass}`,
      ].join(" ")}
    >
      <span className={active ? "" : "font-semibold"}>{value}</span>{" "}
      <span className={active ? "" : "text-slate-500 dark:text-slate-400"}>{label}</span>
    </button>
  );
}

/** Status marker — solid emerald dot for mapped, tone-coloured square for
 *  module-owned, hollow rings for na/unset. AI mapped rules get a violet dot. */
function StatusMark({ status }: { status: ColumnStatus }) {
  if (status.kind === "mapped") {
    if (status.isAI) {
      return (
        <span
          aria-hidden="true"
          title="Mapped (AI)"
          className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500 ring-1 ring-violet-500"
        />
      );
    }
    return (
      <span
        aria-hidden="true"
        title="Mapped"
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 ring-1 ring-emerald-500"
      />
    );
  }
  if (status.kind === "module") {
    const tone = TONE_CLASSES[status.module.tone];
    return (
      <span
        aria-hidden="true"
        title={`Filled by ${status.module.name}`}
        className={[
          "inline-block h-2 w-2 shrink-0 rounded-[2px]",
          tone.mark,
        ].join(" ")}
      />
    );
  }
  if (status.kind === "na") {
    return (
      <span
        aria-hidden="true"
        title="Typically blank. Toggle 'Hide N/A' to dismiss"
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-transparent ring-1 ring-zinc-300"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      title="Unset"
      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-transparent ring-1 ring-slate-300"
    />
  );
}

function SparklesIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z" />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 4.5l3 3 3-3" />
    </svg>
  );
}
